import { Badge, StatusDot, VisuallyHidden } from '@buffer-mono/popcorn'

import { formatCompactNumber } from '~publish/helpers/numberFormatters'

import { useMentionsCounterMode } from '../../hooks/mentions/useMentionsCounterMode'
import type { MentionsCounterMode } from '../../hooks/mentions/useMentionsCounterMode'

import styles from './MentionsTabIndicator.module.css'

import type { JSX } from 'react'

type MentionsTabIndicatorProps = {
  /** Unanswered mention count for the current scope (channel or all channels). */
  count: number | null
}

/**
 * The unanswered-mentions indicator shown on the Mentions tab.
 *
 * - `counter` mode: a number badge (hidden at 0).
 * - `dot` mode: a single dot when any mention is unanswered — cleared only once
 *   every mention is replied to or resolved.
 */
export function MentionsTabIndicator({
  count,
}: MentionsTabIndicatorProps): JSX.Element | null {
  const { mode } = useMentionsCounterMode()

  return <MentionsTabIndicatorView count={count} mode={mode} />
}

export function MentionsTabIndicatorView({
  count,
  mode,
}: MentionsTabIndicatorProps & {
  mode: MentionsCounterMode
}): JSX.Element | null {
  if (count == null || count <= 0) {
    return null
  }

  if (mode === 'dot') {
    return (
      <span className={styles.dot}>
        <StatusDot />
        <VisuallyHidden>unanswered mentions</VisuallyHidden>
      </span>
    )
  }

  return (
    <Badge variant="brand" size="small" className={styles.badge}>
      {formatCompactNumber(count, 99)}
      <VisuallyHidden> unanswered mentions</VisuallyHidden>
    </Badge>
  )
}
